import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { useDispatch, useSelector } from 'react-redux';
import appActions from '../../../appActions';
import PrimeTable from '../../../components/PrimeTable';
import SysErrorCodeFilterForm from './SysErrorCodeFilterForm';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog'; // Path to your reusable component
import { isPermissionCodeValid } from '../../../utils/mics';

const SysErrorCodeList  = ({ pageId }) => {
    
    const readAccess = isPermissionCodeValid(`${pageId}-R`);
    const writeAccess = isPermissionCodeValid(`${pageId}-W`);
    const deleteAccess = isPermissionCodeValid(`${pageId}-D`);

    let navigate = useNavigate();
    const dispatch = useDispatch();
    const [deleteRecord, setDeleteRecord] = useState(null); // Store record for deletion

    const sysErrorCodeList = useSelector((state) => state.sysErrorCodes.sysErrorCodeList);
    //const sysErrorCode = useSelector((state) => state.sysErrorCodes.sysErrorCode);
    const filters = useSelector((state) => state.sysErrorCodes.filters);
    
    const getDefaultParams = (filters = {}) => ({
        ...filters,
        pageno: 1,
        pagesize: 10,
        sort: 'createdOn',
        sortOrder: -1,
    });

    const defaultParams = useMemo(() => getDefaultParams(filters), [filters]);
    
    const defaultParamsWithoutFilters = useMemo(() => getDefaultParams(), []);

    const [formValues, setFormValues] = useState(defaultParams);
    
    const handleFormValuesChange = (values) => {
        setFormValues(values);
    };

    useEffect(() => {
        dispatch(appActions.SYSERRORCODE_GET_ALL_REQUEST(defaultParams));
    }, [dispatch, defaultParams]);

    const handleRefresh = useCallback(() => {
        dispatch(appActions.SYSERRORCODE_GET_ALL_REQUEST(defaultParamsWithoutFilters));
    }, [dispatch, defaultParamsWithoutFilters]);

    const handleSearch = useCallback((params) => {
        const searchParams = {
            ...params,
            pagesize: params.pagesize || defaultParams?.pagesize,
            pageno: params.pageno || defaultParams?.pageno,
            sort: params.sort || defaultParams?.sort,
            sortOrder: params.sortOrder || defaultParams?.sortOrder,
        };
    
        dispatch(appActions.SYSERRORCODE_GET_ALL_REQUEST(searchParams));
    }, [defaultParams, dispatch]);

    const linkToNew = useCallback(
        () => navigate('/ui/system-error-codes/details', { replace: false }),
        [navigate],
    );

    const handleDeleteConfirm = useCallback(() => {
        if (deleteRecord) {
            dispatch(appActions.SYSERRORCODE_DELETE_REQUEST({ id: deleteRecord.id }));
            setDeleteRecord(null); // Clear the record after deletion
    
            // Explicitly refresh the table after deletion
            setTimeout(() => {
                handleRefresh();
            }, 500); // Add a slight delay if needed to allow the backend to process
        }
    }, [dispatch, deleteRecord, handleRefresh])

    const handleDelete = useCallback((record) => {
        setDeleteRecord(record); // Store the record to be deleted
    }, []);

    const renderHeader = () => {
        return (
            <div className="d-flex flex-row gap-10 align-items-center">
                <h1 className="page-header flex">System Error Codes</h1>
                <Button icon="pi pi-sync" data-pr-tooltip="Refresh" rounded text aria-label="Refresh" className='mr-auto tp' onClick={handleRefresh} />
                {writeAccess &&
                    <Button label="Add New" icon="pi pi-plus" className="p-button-outlined tp" data-pr-tooltip="Add New" onClick={linkToNew} />
                }
            </div>
        );
    };

    const handleViewRedirect = useCallback((record) => {
        const data = sysErrorCodeList?.data?.find((t) => t.id === record.id);
        navigate('/ui/system-error-codes/details', {
            state: { data, isView: true },
        });
    }, [navigate, sysErrorCodeList]);

    const handleEditRedirect = useCallback((record) => {
        const data = sysErrorCodeList?.data?.find((t) => t.id === record.id);
        navigate('/ui/system-error-codes/details', { state: { data, isEdit: true } });
    }, [navigate, sysErrorCodeList]);

    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                {readAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                        <i className="pi pi-eye text-blue-500 text-base"></i>
                    </span>
                }
                {writeAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleEditRedirect(rowData)} data-pr-tooltip="Edit">
                        <i className="pi pi-pencil text-blue-500 text-base"></i>
                    </span>
                }
                {deleteAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                        <i className="pi pi-times-circle text-base text-danger" />
                    </span>
                }
            </div>
        ),
        [handleDelete, handleEditRedirect, handleViewRedirect, readAccess, writeAccess, deleteAccess],
    );

    const columns = [
        // { field: 'id', header: 'ID', sortable: true, width: '50px' },
        { field: 'errorCode', header: 'Code', sortable: true, width: '120px' },
        { field: 'errorMsg', header: 'Message', sortable: true, },
        { field: 'errorType', header: 'Type', sortable: true,  width: '120px' },
        { field: 'errorGroup', header: 'Group', sortable: true ,  width: '110px' },
        { field: 'errorLevel', header: 'Level', sortable: true,  width: '110px'  },
        { field: 'alertGroup', header: 'Alert Group', sortable: true,  width: '120px'  },
        { field: 'createdOn', header: 'Created On', sortable: true, width: '140px', columnType: 'datetime' },
        // { field: 'lastUpdated', header: 'Last Updated', sortable: true, columnType : 'datetime'  },
        // { field: 'lastUpdatedBy', header: 'Last Updated By', sortable: true },
    ];

      const onFilterChangeCallback  = useCallback(
            (e) => {
    
                const { sortField, sortOrder, rows, page } = e;
                
                const params = {...formValues, pagesize: rows, pageno: page, sort: sortField, sortOrder: sortOrder };
    
                setFormValues(prevFormValues => ({
                    ...prevFormValues,
                    sort: sortField, 
                    sortOrder: sortOrder,
                    pagesize: rows,
                    pageno: page
                }));
                handleSearch(params); // Call search with updated sorting params
            },
            [handleSearch, formValues],
    );
    
    const renderList = () => {
        return (
            <>
                <div className="pv-15 ph-15">
                    {renderHeader()}
                </div>
                
                <div className="bg-white mx-3 pv-15 ph-15 br-6 shadow-white">
                    <SysErrorCodeFilterForm
                        handleSearch={handleSearch}
                        handleReset={handleRefresh}
                        onFormValuesChange={handleFormValuesChange} 
                    />
                    <div className='custom-devider'></div>
                    <PrimeTable
                        list={sysErrorCodeList}
                        columns={columns}
                        actionColumn={actionTemplate}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        tableName="SystemErrorCode"
                        isLazy={true}
                        showGenerateXmlBtn={false}
                        pageId={ pageId }
                    />
                </div>
            </>
        );
    };

    return (
        <div className="form_height">
            {renderList()}
            {deleteRecord && (
                <ConfirmationDialog
                    message={`Are you sure you want to delete the record with error code: ${deleteRecord.errorCode}?`}
                    onAccept={handleDeleteConfirm}
                    onReject={() => setDeleteRecord(null)}
                />
            )}
        </div>
    );
};

export default SysErrorCodeList;
